// pages/public/ProgramDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { db } from '../../firebase/config';
import { Program } from '../../firebase/programFirestoreService';
import { useAuth } from '../../contexts/AuthContext';
import { cache } from '../../utils/cacheManager';
import { FaCalendarAlt, FaMapMarkerAlt, FaUserTie, FaUsers, FaTag, FaBookOpen, FaChevronLeft, FaChevronRight, FaArrowLeft, FaCheckCircle, FaCalendarCheck, FaClock, FaUserCheck, FaShareAlt, FaCertificate, FaExternalLinkAlt, FaFacebook, FaTwitter, FaWhatsapp, FaLinkedin, FaLink, FaYoutube } from '../../utils/iconUtils';
import { useRef } from 'react';
import CommentSection from '../../components/common/CommentSection';

const ProgramDetail: React.FC = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState<'open' | 'not_yet_opened' | 'closed'>('closed');
  const [relatedPrograms, setRelatedPrograms] = useState<Program[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState<boolean>(false);
  const [viewerIndex, setViewerIndex] = useState<number>(0);
  const [shareOpen, setShareOpen] = useState<boolean>(false);
  const [youtubeUrl, setYoutubeUrl] = useState<string>('');
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const shareRef = useRef<HTMLDivElement | null>(null);

  const extractYoutubeId = (url: string): string => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7] && match[7].length === 11) ? match[7] : '';
  };

  const getYoutubeEmbedUrl = (url: string): string => {
    const videoId = extractYoutubeId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };
  useEffect(() => {
    // reset carousel index when program changes
    setCurrentImageIndex(0);
    setViewerIndex(0);
    setIsViewerOpen(false);
  }, [program?.imageUrl, (program as any)?.otherImages?.length]);

  // Ensure we start at the top when navigating to a program detail
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [programId]);

  const loadProgram = async () => {
    if (!programId) return;
    
    try {
      const programData = await cache.get<Program | null>(`program.${programId}`, async () => {
        const programRef = doc(db, 'programs', programId);
        const programSnap = await getDoc(programRef);
        if (!programSnap.exists()) return null;
        return { id: programSnap.id, ...programSnap.data() } as Program;
      }, 2 * 60 * 60 * 1000);

      if (programData) {
        setProgram(programData);
        if (programData.youtubeUrl) {
          const embedUrl = getYoutubeEmbedUrl(programData.youtubeUrl);
          if (embedUrl) {
            setYoutubeUrl(embedUrl);
            setShowVideo(true);
          } else {
            setYoutubeUrl('');
            setShowVideo(false);
          }
        } else {
          setYoutubeUrl('');
          setShowVideo(false);
        }
        
        const now = new Date();
        const regStart = programData.registrationStart
          ? (typeof (programData.registrationStart as any).toDate === 'function'
              ? (programData.registrationStart as any).toDate()
              : new Date(programData.registrationStart as any))
          : null;

        const regEnd = programData.registrationEnd
          ? (typeof (programData.registrationEnd as any).toDate === 'function'
              ? (programData.registrationEnd as any).toDate()
              : new Date(programData.registrationEnd as any))
          : null;

        let status: 'open' | 'not_yet_opened' | 'closed' = 'closed';
        if (regStart && now < regStart) {
          status = 'not_yet_opened';
        } else if (regEnd && now > regEnd) {
          status = 'closed';
        } else if (regStart && regEnd) {
          status = now >= regStart && now <= regEnd ? 'open' : 'closed';
        } else if (regStart && !regEnd) {
          status = now >= regStart ? 'open' : 'not_yet_opened';
        } else if (!regStart && regEnd) {
          status = now <= regEnd ? 'open' : 'closed';
        } else {
          status = 'closed';
        }

        setRegistrationStatus(status);
        // Only consider registration open when the program requires registration
        setRegistrationOpen(!!programData.requiresRegistration && status === 'open');
        
        // In a real app, you'd fetch related programs based on category/tags
        // For now, we'll just set an empty array
        setRelatedPrograms([]);
      }
    } catch (error) {
      console.error('Error loading program:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProgram();
  }, [programId]);

  // Helper to safely get program images (main image + additional images)
  const getProgramImages = (p?: Program | null) => {
    const extra = (p as any)?.otherImages && Array.isArray((p as any).otherImages) ? (p as any).otherImages : [];
    return [p?.imageUrl, ...extra].filter(Boolean as any) as string[];
  };

  // Auto-swipe carousel every 7 seconds when multiple images exist
  useEffect(() => {
    const images = getProgramImages(program);
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex(i => (i + 1) % images.length);
    }, 7000);

    return () => clearInterval(interval);
  }, [program?.imageUrl, (program as any)?.otherImages?.length]);

  // Keyboard navigation for the lightbox viewer
  useEffect(() => {
    if (!isViewerOpen) return;
    const images = getProgramImages(program);
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsViewerOpen(false);
      if (e.key === 'ArrowRight') setViewerIndex(i => (i + 1) % images.length);
      if (e.key === 'ArrowLeft') setViewerIndex(i => (i - 1 + images.length) % images.length);
    };
    document.addEventListener('keydown', handler);
    // Prevent body scroll while viewer open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prev;
    };
  }, [isViewerOpen, program?.imageUrl, (program as any)?.otherImages?.length]);

  const handleRegister = () => {
    if (!isAuthenticated) {
      alert('Please login to register for this program');
      navigate('/login', { 
        state: { from: `/programs/${programId}/register` } 
      });
      return;
    }
    if (!registrationOpen) {
      if (registrationStatus === 'not_yet_opened') {
        alert('Registration has not yet opened for this program.');
      } else {
        alert('Registration is currently closed for this program.');
      }
      return;
    }
    // Navigate to the registration page when registration is open
    navigate(`/programs/${programId}/register`);
  };

  const shareProgram = async () => {
    const shareData = {
      title: program?.title,
      text: program?.description,
      url: window.location.href,
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Build social share URLs for the current page
  const getShareUrls = () => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(program?.title || '');
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      whatsapp: `https://api.whatsapp.com/send?text=${title}%20${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      mail: `mailto:?subject=${title}&body=${url}`
    };
  };

  // Toggle share menu; prefer native share when available
  const onShareClick = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: program?.title, text: program?.description, url: window.location.href });
        return;
      } catch (e) {
        // fallback to showing menu
      }
    }
    setShareOpen(s => !s);
  };

  // close share menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!shareRef.current) return;
      if (!(e.target instanceof Node)) return;
      if (!shareRef.current.contains(e.target)) setShareOpen(false);
    };
    if (shareOpen) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [shareOpen]);

  const getProgramUrl = () => `${window.location.origin}/programs/${program?.id}`;

  const openShareLink = async (platform: 'facebook' | 'twitter' | 'whatsapp' | 'linkedin' | 'copy') => {
    const url = getProgramUrl();
    const title = program?.title || 'Program';
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedMessage = encodeURIComponent(`${title} ${url}`);

    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
      } catch (e) {
        alert(url);
      }
      setShareOpen(false);
      return;
    }

    let shareUrl = '';
    if (platform === 'facebook') shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    else if (platform === 'twitter') shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
    else if (platform === 'whatsapp') shareUrl = `https://api.whatsapp.com/send?text=${encodedMessage}`;
    else if (platform === 'linkedin') shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;

    if (shareUrl) window.open(shareUrl, '_blank', 'noopener,noreferrer,width=620,height=700');
    setShareOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-8"></div>
            <div className="h-96 bg-gray-200 rounded-xl mb-8"></div>
            <div className="space-y-4">
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
              <div className="h-6 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
if (!program) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center py-12">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaBookOpen className="text-3xl text-primary-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Program Not Found</h2>
          <p className="text-gray-600 mb-6">
            The program you're looking for doesn't exist or has been removed.
          </p>
          <Link 
            to="/programs" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium group"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to All Programs
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const toDateObj = (d: any): Date | null => {
      try {
        if (!d) return null;
        if (d instanceof Date) return d;
        if (typeof d?.toDate === 'function') return d.toDate();
        if (typeof d === 'object' && d !== null && 'seconds' in d && typeof d.seconds === 'number') {
          return new Date(d.seconds * 1000);
        }
        if (typeof d === 'number' || typeof d === 'string') return new Date(d);
        return null;
      } catch (e) {
        return null;
      }
    };

    const dateObj = toDateObj(date);
    if (!dateObj || isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (date: any) => {
    if (!date) return 'N/A';
    const toDateObj = (d: any): Date | null => {
      try {
        if (!d) return null;
        if (d instanceof Date) return d;
        if (typeof d?.toDate === 'function') return d.toDate();
        if (typeof d === 'object' && d !== null && 'seconds' in d && typeof d.seconds === 'number') {
          return new Date(d.seconds * 1000);
        }
        if (typeof d === 'number' || typeof d === 'string') return new Date(d);
        return null;
      } catch (e) {
        return null;
      }
    };

    const dateObj = toDateObj(date);
    if (!dateObj || isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCarouselStatus = (): 'upcoming' | 'ongoing' | 'completed' => {
    const toDateObj = (d: any): Date | null => {
      try {
        if (!d) return null;
        if (d instanceof Date) return d;
        if (typeof d?.toDate === 'function') return d.toDate();
        if (typeof d === 'object' && d !== null && 'seconds' in d && typeof d.seconds === 'number') {
          return new Date(d.seconds * 1000);
        }
        if (typeof d === 'number' || typeof d === 'string') return new Date(d);
        return null;
      } catch (e) {
        return null;
      }
    };

    const now = new Date();
    const start = toDateObj(program.startDate);
    const end = toDateObj(program.endDate);

    if (start && now < start) return 'upcoming';
    if (end && now > end) return 'completed';
    if (start && end && now >= start && now <= end) return 'ongoing';

    // Fallback when dates are missing/invalid
    if (program.status === 'completed') return 'completed';
    if (program.status === 'ongoing') return 'ongoing';
    return 'upcoming';
  };

  const carouselStatus = getCarouselStatus();

  // Derived flags for button state
  const isFull = !!(program?.capacity && (program.registeredCount || 0) >= program.capacity);
  const rddFlag = Boolean((program as any)?.rdd);

  // Manual tweak for carousel image vertical focus (0 = top, 50 = center, 100 = bottom)
  const carouselImagePositionY = 20;

  // Combined images for carousel/lightbox
  const images = getProgramImages(program);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-b from-gray-50 to-white"
    >
      {/* Hero Section Full Width */}
      <motion.div
        initial={{ scale: 0.98, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="relative h-[24rem] md:h-[30rem] lg:h-[34rem] overflow-hidden text-white"
        onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
        onTouchMove={(e) => setTouchEndX(e.touches[0].clientX)}
        onTouchEnd={() => {
          if (touchStartX !== null && touchEndX !== null) {
            const delta = touchEndX - touchStartX;
            if (delta > 50) {
              setCurrentImageIndex(i => Math.max(0, i - 1));
            } else if (delta < -50) {
              setCurrentImageIndex(i => (images.length ? (i + 1) % images.length : 0));
            }
          }
          setTouchStartX(null);
          setTouchEndX(null);
        }}
      >
        {images.length > 0 ? (
          images.map((src: string, idx: number) => (
            <img
              key={src + idx}
              src={src}
              alt={`${program?.title || 'program'}-${idx}`}
              onClick={() => {
                setViewerIndex(idx);
                setIsViewerOpen(true);
              }}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${idx === currentImageIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'} cursor-pointer`}
              style={{ objectPosition: `center ${carouselImagePositionY}%` }}
            />
          ))
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-600 to-accent-500 flex items-center justify-center">
            <FaBookOpen className="text-6xl text-white/80" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/20 pointer-events-none"></div>

        <div className="absolute top-4 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-3">
            <Link
              to="/programs"
              className="inline-flex items-center text-white hover:text-accent-300 group transition-colors bg-black/35 px-3 py-2 rounded-lg backdrop-blur-sm"
            >
              <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Programs
            </Link>

            {images.length > 0 && (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewerIndex(currentImageIndex);
                  setIsViewerOpen(true);
                }}
                className="bg-black/60 hover:bg-black/80 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 backdrop-blur-sm border border-white/20 shadow-lg"
                aria-label="View all images"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm font-medium">
                  View Images {images.length > 1 && `(${images.length})`}
                </span>
              </button>
            )}
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-between px-4 z-20 pointer-events-none">
          {images.length > 1 && (
            <button
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex(i => (i - 1 + images.length) % images.length);
              }}
              className="bg-black/40 text-white p-2 rounded-full hover:bg-black/50 pointer-events-auto"
              aria-label="Previous image"
            >
              <FaChevronLeft />
            </button>
          )}
          <div />
          {images.length > 1 && (
            <button
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex(i => (i + 1) % images.length);
              }}
              className="bg-black/40 text-white p-2 rounded-full hover:bg-black/50 pointer-events-auto"
              aria-label="Next image"
            >
              <FaChevronRight />
            </button>
          )}
        </div>

        <div className="absolute bottom-40 md:bottom-44 left-0 right-0 flex items-center justify-center gap-2 z-20">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentImageIndex(idx)}
              className={`w-2 h-2 rounded-full ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none">
          <div className="max-w-7xl mx-auto px-4 pb-8 md:pb-10 pt-20 pointer-events-none">
            <div className="max-w-4xl">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">
                  {program.category}
                </span>
                {program.isFeatured && (
                  <span className="px-3 py-1.5 bg-accent-500 text-primary-900 rounded-full text-sm font-medium flex items-center gap-1">
                    <FaCheckCircle />
                    Featured
                  </span>
                )}
                <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${
                  carouselStatus === 'upcoming'
                    ? 'bg-blue-500 text-white'
                    : carouselStatus === 'ongoing'
                    ? 'bg-accent-500 text-primary-900'
                    : 'bg-gray-600 text-white'
                }`}>
                  {carouselStatus.charAt(0).toUpperCase() + carouselStatus.slice(1)}
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-4xl font-bold leading-tight text-yellow-300">
                {program.title}
              </h1>

              <div className="flex flex-wrap gap-4 pt-4 text-white/95">
                <div className="flex items-center gap-2">
                  <FaCalendarCheck className="text-accent-300" />
                  <span>{formatDate(program.startDate)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-accent-300" />
                  <span>{program.location}</span>
                </div>
                {/* {program.capacity && (
                  <div className="flex items-center gap-2">
                    <FaUsers className="text-accent-300" />
                    <span>{program.capacity} capacity</span>
                  </div>
                )} */}
                {program.youtubeUrl && (
                  <div className="flex items-center gap-2">
                    <FaYoutube className="text-red-400" />
                    <span>Video Available</span>
                  </div>
                )}
              </div>

              {(program.description || program.content) && (
                <p className="mt-4 w-full text-sm md:text-base text-white/90 leading-relaxed whitespace-normal break-words">
                  {program.description || program.content}
                </p>
              )}

              <div className="flex flex-wrap gap-3 pt-5 items-center pointer-events-auto">
                {program.requiresRegistration ? (
                  <button
                    onClick={handleRegister}
                    disabled={!registrationOpen || isFull || rddFlag}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                      (!registrationOpen || isFull || rddFlag)
                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                        : 'bg-accent-400 text-primary-900 hover:bg-accent-500 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {registrationStatus === 'not_yet_opened' ? (
                      <>
                        <FaClock />
                        Not Yet Opened
                      </>
                    ) : !registrationOpen ? (
                      <>
                        <FaClock />
                        Registration Closed
                      </>
                    ) : isFull ? (
                      <>
                        <FaUsers />
                        Program Full
                      </>
                    ) : (
                      <>
                        <FaUserCheck />
                        Register Now
                      </>
                    )}
                  </button>
                ) : (
                  <div className="px-6 py-3 rounded-lg bg-white/10 text-white font-medium flex items-center gap-2">
                    <FaUserCheck />
                    Registration Not Required
                  </div>
                )}

                <div className="relative z-30" ref={shareRef}>
                  <button
                    onClick={(e) => { e.stopPropagation(); onShareClick(); }}
                    className="px-6 py-3 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg font-medium flex items-center gap-2 transition-colors"
                  >
                    <FaShareAlt />
                    Share
                  </button>

                  {shareOpen && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute right-0 bottom-full mb-2 w-56 rounded-lg bg-black/85 backdrop-blur-sm border border-white/20 p-2 shadow-xl z-50 text-sm text-white"
                    >
                      <div>
                        {(() => {
                          const urls = getShareUrls();
                          return (
                            <div className="grid grid-cols-1 gap-1">
                              <a href={urls.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 rounded hover:bg-white/10 transition-colors">
                                <FaFacebook className="text-blue-400" />
                                <span>Share on Facebook</span>
                              </a>
                              <a href={urls.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 rounded hover:bg-white/10 transition-colors">
                                <FaTwitter className="text-sky-400" />
                                <span>Share on Twitter</span>
                              </a>
                              <a href={urls.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 rounded hover:bg-white/10 transition-colors">
                                <FaWhatsapp className="text-green-400" />
                                <span>Share on WhatsApp</span>
                              </a>
                              <a href={urls.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 rounded hover:bg-white/10 transition-colors">
                                <FaLinkedin className="text-blue-300" />
                                <span>Share on LinkedIn</span>
                              </a>
                              <button
                                onClick={(ev) => { ev.stopPropagation(); navigator.clipboard.writeText(window.location.href); alert('Link copied to clipboard!'); setShareOpen(false); }}
                                className="flex items-center gap-3 px-3 py-2 rounded hover:bg-white/10 transition-colors text-left w-full"
                              >
                                <FaLink className="text-gray-300" />
                                <span>Copy link</span>
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      {/* Lightbox Viewer Modal */}
      {isViewerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <button
            className="absolute top-6 right-6 text-white text-2xl p-2 rounded-full bg-black/30"
            aria-label="Close viewer"
            onClick={() => setIsViewerOpen(false)}
          >
            X
          </button>

          <div className="relative max-w-6xl w-full mx-4">
            <div className="relative h-[70vh] md:h-[80vh] flex items-center justify-center">
              <button
                className="absolute left-2 md:left-6 z-20 text-white bg-black/30 p-3 rounded-full"
                onClick={() => setViewerIndex(i => (i - 1 + images.length) % images.length)}
                aria-label="Previous image"
              >
                <FaChevronLeft />
              </button>

              <img
                src={images[viewerIndex]}
                alt={`viewer-${viewerIndex}`}
                className="max-h-[70vh] md:max-h-[80vh] w-auto max-w-full object-contain mx-auto"
              />

              <button
                className="absolute right-2 md:right-6 z-20 text-white bg-black/30 p-3 rounded-full"
                onClick={() => setViewerIndex(i => (i + 1) % images.length)}
                aria-label="Next image"
              >
                <FaChevronRight />
              </button>
            </div>

            <div className="mt-4 flex items-center justify-center gap-2 overflow-x-auto">
              {images.map((src: string, idx: number) => (
                <button
                  key={src + idx + '-thumb'}
                  onClick={() => setViewerIndex(idx)}
                  className={`w-16 h-12 flex-shrink-0 overflow-hidden rounded ${idx === viewerIndex ? 'ring-2 ring-white' : ''}`}
                >
                  <img src={src} alt={`thumb-${idx}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Right Column - Main Content */}
          <div className="lg:w-2/3 lg:order-2">
            {(program.description || program.content) && (
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-white/70 backdrop-blur-sm border border-gray-100 rounded-xl shadow-sm p-6 mb-8"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                  Programme Content
                </h2>
             
                {program.content && program.content !== program.description && (
                  <div className="text-gray-600 text-base whitespace-pre-line leading-relaxed">
                    {program.content}
                  </div>
                )}
              </motion.div>
            )}

          
          
            {programId && (
              <div className="mt-12">
                <CommentSection targetType="program" targetId={program.id || programId} />
              </div>
            )}
          </div>

          {/* Left Column - Sidebar */}
          <div className="lg:w-1/2 lg:order-1">
            {showVideo && youtubeUrl && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-red-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <FaYoutube className="mr-2 text-red-500" />
                    Watch Video
                  </h3>
                  <button
                    onClick={() => setShowVideo(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Hide Video
                  </button>
                </div>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg shadow-lg">
                  <iframe
                    src={youtubeUrl}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute top-0 left-0 w-full h-full"
                  />
                </div>
                {program.youtubeUrl && (
                  <p className="text-sm text-gray-500 mt-2">
                    You can also
                    <a
                      href={program.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800 ml-1"
                    >
                      watch directly on YouTube
                    </a>.
                  </p>
                )}
              </motion.div>
            )}

            {!showVideo && program.youtubeUrl && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-8"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaYoutube className="text-red-500 text-2xl mr-3" />
                    <div>
                      <h4 className="font-bold text-gray-900">Video Content Available</h4>
                      <p className="text-sm text-gray-600">This program includes a related video</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowVideo(true)}
                    className="btn-primary flex items-center"
                  >
                    <FaYoutube className="mr-2" />
                    Watch Video
                  </button>
                </div>
              </motion.div>
            )}

            {/* Quick Facts Card */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-primary-600 to-primary-800 text-white rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg text-white font-bold mb-4">Quick Facts</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-white/20">
                  <span className="text-white/80">Category</span>
                  <span className="font-medium">{program.category}</span>
                </div>
                <div className="flex items-center justify-between pb-3 border-b border-white/20">
                  <span className="text-white/80">Duration</span>
                  <span className="font-medium">
                    {program.startDate && program.endDate ? 
                      `${formatDate(program.startDate)} - ${formatDate(program.endDate)}` : 
                      'Flexible'
                    }
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/80">Registration</span>
                  <span className="font-medium">
                    {program.requiresRegistration ? 'Required' : 'Not Required'}
                  </span>
                </div>
              </div>
            </motion.div>
            {/* Tags Card */}
            {program.tags && program.tags.length > 0 && (
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl shadow-lg p-6 mb-8 mt-6 border border-gray-100"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {program.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

               {/* Details Card */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-lg p-6 mb-8 mt-6 border border-gray-100"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100">
                Program Details
              </h2>
              
              <div className="grid grid-cols-1 gap-6">
                {/* Date & Time */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-500">
                    <FaCalendarAlt />
                    <span className="font-medium">Date & Time</span>
                  </div>
                  <div className="text-gray-900">
                    <p className="font-medium">{formatDateTime(program.startDate)}</p>
                    {program.endDate && (
                      <p className="text-sm text-gray-600">to {formatDateTime(program.endDate)}</p>
                    )}
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-500">
                    <FaMapMarkerAlt />
                    <span className="font-medium">Location</span>
                  </div>
                  <p className="text-gray-900 font-medium">{program.location}</p>
                </div>

                {/* Organizer */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-500">
                    <FaUserTie />
                    <span className="font-medium">Organizer</span>
                  </div>
                  <p className="text-gray-900 font-medium">{program.organizer}</p>
                </div>

             
              </div>
            </motion.div>

  {/* Registration Info Card */}
            {program.requiresRegistration && (
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl shadow-lg p-6 mb-8 border border-primary-100"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <FaCertificate className="text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Registration Information</h3>
                    <p className="text-sm text-gray-600">Important information for participants</p>
                  </div>
                </div>

                {program.registrationStart && program.registrationEnd && (
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <FaClock className="text-primary-600" />
                      <span className="font-medium text-gray-900">Registration Period</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm text-gray-600">Starts</p>
                        <p className="font-medium">{formatDateTime(program.registrationStart)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Ends</p>
                        <p className="font-medium">{formatDateTime(program.registrationEnd)}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Registration Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      registrationStatus === 'open'
                        ? 'bg-green-100 text-green-800'
                        : registrationStatus === 'not_yet_opened'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {registrationStatus === 'open'
                        ? 'Open'
                        : registrationStatus === 'not_yet_opened'
                        ? 'Not Yet Opened'
                        : 'Closed'}
                    </span>
                  </div>
          
                </div>
              </motion.div>
            )}

   

          
          </div>
        </div>

        {/* Related Programs Section */}
        {relatedPrograms.length > 0 && (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-12"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">You Might Also Like</h2>
              <Link 
                to="/programs" 
                className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-2"
              >
                View All
                <FaExternalLinkAlt />
              </Link>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPrograms.map((relatedProgram) => (
                <Link 
                  key={relatedProgram.id}
                  to={`/programs/${relatedProgram.id}`}
                  className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden"
                >
                  <div className="h-40 bg-gray-200 overflow-hidden">
                    {relatedProgram.imageUrl ? (
                      <img 
                        src={relatedProgram.imageUrl} 
                        alt={relatedProgram.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center">
                        <FaBookOpen className="text-3xl text-primary-600" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {relatedProgram.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {relatedProgram.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default ProgramDetail;


